import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Paper,
  alpha,
  Stack,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ChevronRight,
  SettingsOutlined,
  StraightenOutlined,
  NotificationsNoneOutlined,
  TuneOutlined,
  AutoAwesomeOutlined,
  PublicOutlined,
  ForumOutlined,
  SummarizeOutlined,
  BarChartOutlined,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { withRouter } from 'react-router';

import MeasurmentConfig from './Measurements/index';
import NotificationConfig from './Notification';
import PreferencesConfig from './Preferences';
import { AiModels, FeatureSettings, GlobalSettings, UsageLog } from './AiSettings';

const ConfigurationContent = withRouter(({ userCategory, userPermission, location, history }) => {
  const userCat = userCategory?.trim()?.toLowerCase();
  const userPerm = userPermission?.map((mp) => mp.trim().toLowerCase()) || [];

  const canViewSystemConfigurations = userPerm.includes('manage_system_configurations');
  const isEDM = userCat === 'edm';
  const canManageAi = userPerm.includes('manage_ai_settings');

  // Menu groups; each page has its own ?tab= value
  const groups = [
    {
      label: 'General',
      items: [
        {
          tab: 'measurement',
          label: 'Measurement',
          icon: <StraightenOutlined fontSize="small" />,
          render: () => <MeasurmentConfig />,
          visible: isEDM && canViewSystemConfigurations,
        },
        {
          tab: 'notifications',
          label: 'Notifications',
          icon: <NotificationsNoneOutlined fontSize="small" />,
          render: () => <NotificationConfig />,
          visible: isEDM && canViewSystemConfigurations,
        },
        {
          tab: 'preferences',
          label: 'Preferences',
          icon: <TuneOutlined fontSize="small" />,
          render: () => <PreferencesConfig />,
          visible: true,
        },
      ],
    },
    {
      label: 'AI Settings',
      items: [
        {
          tab: 'ai-global',
          label: 'Global',
          icon: <PublicOutlined fontSize="small" />,
          render: () => <GlobalSettings />,
          visible: canManageAi,
        },
        {
          tab: 'ask-ai',
          label: 'Ask AI',
          icon: <ForumOutlined fontSize="small" />,
          render: () => <FeatureSettings feature="ASSISTANT" />,
          visible: canManageAi,
        },
        {
          tab: 'ai-reporting',
          label: 'AI Report',
          icon: <SummarizeOutlined fontSize="small" />,
          render: () => <FeatureSettings feature="AI_REPORT" />,
          visible: canManageAi,
        },
        {
          tab: 'ai-usage',
          label: 'Usage Log',
          icon: <BarChartOutlined fontSize="small" />,
          render: () => <UsageLog />,
          visible: canManageAi,
        },
        {
          tab: 'ai-models',
          label: 'AI Models',
          icon: <AutoAwesomeOutlined fontSize="small" />,
          render: () => <AiModels />,
          visible: isEDM && canViewSystemConfigurations,
        },
      ],
    },
  ]
    .map((group) => ({ ...group, items: group.items.filter((item) => item.visible) }))
    .filter((group) => group.items.length > 0);

  const allItems = groups.flatMap((group) => group.items);
  const searchParams = new URLSearchParams(location?.search);
  const requestedTab = searchParams.get('tab');
  const activeItem = allItems.find((item) => item.tab === requestedTab) || allItems[0];

  const goToTab = (tab, replace = false) => {
    const params = new URLSearchParams(location?.search);
    params.set('tab', tab);
    const next = { search: params.toString() };
    if (replace) history.replace(next);
    else history.push(next);
  };

  // Missing or unknown ?tab= -> show the first page the user can see, and put it in the URL
  useEffect(() => {
    if (activeItem && requestedTab !== activeItem.tab) goToTab(activeItem.tab, true);
  }, [requestedTab, activeItem?.tab]);

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#F4F6F8', minHeight: '100vh' }}>
      {/* HEADER & BREADCRUMBS */}
      <Box sx={{ mb: 3 }}>
        {isEDM && (
          <Breadcrumbs separator={<ChevronRight fontSize="small" />} sx={{ mb: 1 }}>
            <Link to="/settings" style={{ textDecoration: 'none' }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Settings
              </Typography>
            </Link>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Configuration Settings
            </Typography>
          </Breadcrumbs>
        )}

        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: '16px',
            border: '1px solid',
            borderColor: 'divider',
            background: '#fff',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
            <Box
              sx={{
                p: 1,
                display: 'flex',
                borderRadius: '10px',
                bgcolor: alpha('#1976d2', 0.1),
                color: 'primary.main',
              }}
            >
              <SettingsOutlined fontSize="small" />
            </Box>
            <Typography variant="h5" fontWeight={700} color="text.primary">
              Configuration Settings
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Define result measurements & parameters, personal & system wide preferences
          </Typography>
        </Paper>
      </Box>

      {/* MAIN CONTENT CARD */}
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          borderRadius: '20px',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          minHeight: '70vh',
          bgcolor: '#fff',
        }}
      >
        {/* GROUPED NAVIGATION SIDEBAR */}
        <Box
          component="nav"
          sx={{
            width: { xs: '100%', md: 220 },
            flexShrink: 0,
            borderRight: { md: '1px solid lightgray' },
            borderBottom: { xs: '1px solid', md: 'none' },
            borderColor: 'divider',
            bgcolor: alpha('#F4F6F8', 0.4),
            py: 1.5,
          }}
        >
          {groups.map((group) => (
            <List
              key={group.label}
              dense
              disablePadding
              sx={{ px: 1.5, mb: 1 }}
              subheader={
                <Typography
                  variant="caption"
                  sx={{
                    px: 1.5,
                    pt: 1.5,
                    pb: 0.75,
                    display: 'block',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    letterSpacing: 1.2,
                    color: 'text.disabled',
                  }}
                >
                  {group.label}
                </Typography>
              }
            >
              {group.items.map((item) => {
                const isActive = item.tab === activeItem?.tab;
                return (
                  <ListItemButton
                    key={item.tab}
                    selected={isActive}
                    onClick={() => !isActive && goToTab(item.tab)}
                    sx={{
                      borderRadius: '10px',
                      mb: 0.25,
                      color: isActive ? 'primary.main' : 'text.secondary',
                      '&.Mui-selected': {
                        bgcolor: alpha('#1976d2', 0.08),
                        '&:hover': { bgcolor: alpha('#1976d2', 0.12) },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 34, color: 'inherit' }}>{item.icon}</ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: isActive ? 700 : 600 }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          ))}
        </Box>

        {/* CONTENT */}
        <Box sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, md: 3 }, overflowY: 'auto' }}>
          {/* key remounts the page when switching, so each page starts fresh */}
          {activeItem && <Box key={activeItem.tab}>{activeItem.render()}</Box>}
        </Box>
      </Paper>
    </Box>
  );
});

export default ConfigurationContent;
